import { Component, inject, Input, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { downloadFile } from '../../../functions/download-file';
import { DistributedReport } from '../../../models/api/reports/distributed-report';
import { DistributedReportCriteria } from '../../../models/api/reports/distributed-report-criteria';
import { ReportsService } from '../../../services/reports/reports.service';
import { BaseComponent } from '../../base/base.component';
import { BackButtonComponent } from '../../core/buttons/back-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HomeCardComponent } from '../../home/home-card.component';
import { DistributedRequestViewComponent } from './distributed-request-view.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-distributed-report-page',
  imports: [
    RouterLink,
    BackButtonComponent,
    ActionRowComponent,
    ButtonComponent,
    PageHeaderComponent,
    DistributedRequestViewComponent,
    EmptyStateComponent,
    LoadingPanelComponent,
    HomeCardComponent,
  ],
  template: `
    @if (report) {
      <app-page-header
        [label]="report.reportName"
        labelHeader="Distributed report"
        labelCase="none"
        headerSize="h1"
      >
        <ng-template #right>
          <app-action-row>
            <app-button
              right1
              label="Download"
              icon="fa-light fa-arrow-down-to-line"
              title="Download report file"
              (click)="downloadReport()"
            /> </app-action-row
        ></ng-template>
      </app-page-header>

      <div class="tw-flex">
        <app-back-button
          left1
          routerLink="{{ route }}"
          [queryParams]="{ tab: 'completed' }"
        >
          Back
        </app-back-button>
      </div>
    }
    <div class="tw-grow tw-justify-center tw-w-full">
      <div class="tw-min-w-0 tw-w-[100%] tw-pt-4 sm:tw-pt-8 tw-mb-4">
        <app-home-card type="Large">
          <div class="tw-min-w-0 tw-w-[100%] tw-p-4 sm:tw-p-8">
            <app-loading-panel
              [state]="loading ? 'Loading' : 'Loaded'"
              loadingStyle="Box"
              [loadingHeight]="500"
              loadingClasses="tw-pb-8"
              [error]="error"
            >
              <ng-template #loaded>
                @if (report) {
                  <div class="tw-pb-8">
                    <app-distributed-request-view
                      [dataSource]="report"
                      [criteria]="criteria"
                      [externalAccess]="externalAccess"
                    />
                  </div>
                } @else {
                  <app-empty-state
                    icon="fa-light fa-file-circle-xmark"
                    text="Report not found"
                    helpText="The distributed report you are looking for could not be loaded."
                  />
                }
              </ng-template>
            </app-loading-panel>
          </div>
        </app-home-card>
      </div>
    </div>
  `,
  styles: ``,
})
export class DistributedReportPageComponent
  extends BaseComponent
  implements OnInit
{
  @Input()
  externalAccess = false;

  private readonly reportsService = inject(ReportsService);

  report: DistributedReport;
  criteria: DistributedReportCriteria | null = null;

  loading = false;
  error: any = null;
  route = '/reports';

  @HandleLoadingState()
  async ngOnInit() {
    // Don't remove mockup data, is required for Demo and new Screens Development.
    if (this.externalAccess) {
      this.route = '/external-distributed-reports';
    }

    // Try to get report from service (normal navigation or sessionStorage restore)
    const report = this.reportsService.currentDetailReport() as DistributedReport;

    // If not available, redirect back to reports list
    if (!report) {
      this.router.navigate([this.route], { replaceUrl: true });
      return;
    }

    this.report = report;

    // Load report criteria
    if (this.report?.reportId) {
      this.criteria = await this.api.reports.getDistributedReportCriteria(
        this.report.reportId,
      );
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async downloadReport() {
    if (!this.report) return;

    const blob = await this.api.reports.downloadDistributedReport(
      this.report.sourceSystemReportId,
      this.report.sourceSystemCode,
      this.report.fileName,
    );

    downloadFile(blob, this.report.fileName, blob.type);
  }
}
